/**
 * The page masthead: a back control, the screen's title, and the menu button.
 *
 * THERE IS NO BRAND BAR ANY MORE. This used to render the PlantMe Wallet lockup on
 * every authenticated screen, which spent the most valuable row on the phone
 * telling the user which app they had already opened. The home now opens straight
 * onto the account — its own header carries the switcher, the address, the tier
 * and the menu — and this bar appears only on screens that register a header,
 * where it earns its place by holding the title and the way back.
 *
 * LAYOUT. Three cells: the back control, the title, the menu button. The two
 * flanks are the same width, so the title centres on the BAR rather than on the
 * space left beside it, and both controls stay in flow — which is what keeps them
 * from overlapping the title on a narrow phone.
 *
 * IT ALSO OWNS THE data-masthead FLAG, because it is the only thing that knows
 * whether a bar is on screen. #main cancels its own top inset when one is present
 * (the bar supplies that buffer) and restores it when there is not, so the home's
 * first row does not sit against the status bar.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Chevron from '@/components/Chevron';
import MenuButton from '@/components/MenuButton';
import { useRegisteredPageHeader } from '@/lib/pageHeader';
import { useT } from '@/i18n';

export default function AppHeader() {
  const t = useT();
  const navigate = useNavigate();
  // The header the mounted view registered, or null on the home, which has none.
  const page = useRegisteredPageHeader();
  const hasBar = page !== null;

  /*
   * Two flags, because there are three shells and one boolean cannot describe
   * them: signed out (no chrome), signed in with a bar, and signed in without one
   * — the home. `data-masthead` still means strictly "a bar is on screen", which
   * is what reserves its height and cancels #main's own inset. `data-shell="bare"`
   * marks the third case so the home can take a top inset sized to the notch
   * rather than the bar it does not have.
   */
  useEffect(() => {
    const body = document.body;
    if (hasBar) {
      body.setAttribute('data-masthead', '');
      body.removeAttribute('data-shell');
    } else {
      body.removeAttribute('data-masthead');
      body.setAttribute('data-shell', 'bare');
    }
    return () => {
      body.removeAttribute('data-masthead');
      body.removeAttribute('data-shell');
    };
  }, [hasBar]);

  // The home carries its own header inside the content column; there is no bar to
  // put above it.
  if (!page) return null;

  const showBack = page.back !== false;

  return (
    <header className="app-header" data-mode="page">
      <div className="app-header__inner">
        {showBack ? (
          <button
            type="button"
            className="app-header__back"
            aria-label={page.backLabel ?? t('common.back')}
            onClick={() => {
              // A registered handler wins: on a multi-step flow back means the
              // previous step, not the previous URL. Then a declared parent
              // route, then history.
              if (page.onBack) page.onBack();
              else if (page.backTo) navigate(page.backTo);
              else navigate(-1);
            }}
          >
            {/* The shared compact chevron, mirrored by the stylesheet into a back
                arrow — the same glyph the menu pages use, so two screens one tap
                apart do not show different arrows. */}
            <Chevron className="app-header__back-glyph" />
          </button>
        ) : (
          /* Balances the menu button so the title centres on the BAR rather than on
             the space left beside it. Hidden from assistive tech and not
             focusable, so it adds nothing to the reading or tab order. */
          <span className="app-header__balance" aria-hidden="true" />
        )}
        <h1 className="app-header__page-title">{page.title}</h1>
        <MenuButton className="app-header__menu" />
      </div>
    </header>
  );
}
